import { Box, Text, VStack, Link, HStack, StackDivider } from '@chakra-ui/react';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsLayout from 'presentation/layout/modules/settings/SettingsLayout';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import AppCard from 'presentation/components/shared/card/AppCard';
import { Link as ReactLink } from 'react-router-dom';
import { useFetchNotificationListQuery, useMarkNotificationsAsBulkSeenMutation } from 'rtkQuery/sharespro/queries/notifications';
import AppRoute from 'router/AppRoute';
import moment from 'moment';
import useQueryParam from 'hooks/shared/useQueryParam';
import PaginationContentWrap from 'presentation/components/shared/pagination/PaginationContentWrap';

const NotificationsListPage: FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useQueryParam<number>('page', {
    ifAbsent: 1,
    provider: (val) => parseInt(val),
  });

  const { data, isFetching } = useFetchNotificationListQuery({ page });
  const [markAsBulkSeen] = useMarkNotificationsAsBulkSeenMutation();

  useEffect(() => {
    if (data?.items) {
      const unseenIds = data.items
        .filter(n => !n.read && !n.seen)
        .map(n => n.id);

      if (unseenIds.length > 0) {
        markAsBulkSeen({ ids: unseenIds.join(',') });
      }
    }
  }, [data, markAsBulkSeen]);

  return (
    <SettingsLayout>
      <GridHeading>{t('notifications')}</GridHeading>
      <AppCard w="100%" p="20px">
        <PaginationContentWrap
          itemsCount={data?.items.length ?? 0}
          isFetching={isFetching}
          setPage={setPage}
          currentPage={data?.currentPage ?? 1}
          pageCount={data?.pageCount ?? 1}
        >
          <VStack align="stretch" spacing={0} divider={<StackDivider borderColor="gray.200" />}>
            {data?.items.map((n) => (
              <Link key={n.id} as={ReactLink} to={AppRoute.notificationDetail(n.id)} _hover={{ textDecoration: 'none' }}>
                <Box p={3} _hover={{ bg: 'gray.50' }} opacity={n.read ? 0.6 : 1} bg={n.read ? 'gray.25' : 'transparent'}>
                  <HStack align="flex-start" justify="space-between" spacing={4}>
                    <HStack align="flex-start" spacing={3}>
                      <Box>
                        <Text fontWeight={n.read ? 400 : 600} color={n.read ? 'gray.500' : 'black'}>{n.title}</Text>
                        <Text color={n.read ? 'gray.400' : 'gray.600'} noOfLines={2}>{n.body}</Text>
                      </Box>
                    </HStack>
                    <Text fontSize="sm" color={n.read ? 'gray.500' : 'gray.600'} whiteSpace="nowrap">{moment(n.createdAt).format('DD.MM.YYYY HH:mm')}</Text>
                  </HStack>
                </Box>
              </Link>
            ))}
          </VStack>
        </PaginationContentWrap>
      </AppCard>
    </SettingsLayout>
  );
};

export default NotificationsListPage;
